import { MODULES } from 'src/config/modules'
import { useAnalytics } from 'src/composables/useAnalytics'
import { namespacedValue, TIME_STUDY_RECORD_TYPE } from 'src/utils/analytics'

// Shared, config-driven 'Stage Entered' tracking guard — not Time-Study-specific.
// Adding stage tracking to another module means adding a moduleCode entry here, not
// writing a new guard file. See docs/Behavioral_Analysis_Mixpanel.md §4c.
//
// `stage` defaults to the route name itself when a route entry doesn't override it,
// so most entries only need `funnelStage`.
//
// `recordType` is per-module, not shared — each module's record-type prefix is
// independent of its moduleCode (Time Study's is 'time_study', not 'station_design',
// per docs/Behavioral_Analysis_Mixpanel.md §4b), so a future module supplies its own record-
// type constant from src/utils/analytics.js here rather than reusing Time Study's.
const STAGE_TRACKING_CONFIG = {
  [MODULES.STATION_DESIGN.moduleCode]: {
    recordType: TIME_STUDY_RECORD_TYPE,
    routes: {
      cycles: { funnelStage: 'mark_cycle' },
      divide: { funnelStage: 'divide' },
      steps: { funnelStage: 'define_steps' },
      timeStudy: { stage: 'time_study', funnelStage: 'results' },
      outline: { funnelStage: 'results' },
      improvements: { funnelStage: 'results' },
      audit: { funnelStage: 'results' },
      suggestions: { funnelStage: 'results' },
      notes: { funnelStage: 'results' }
    }
  }
  // future modules add their own top-level key here, e.g. [MODULES.ERGONOMICS.moduleCode]: {...}
}

export function stageTrackingGuard(to, from, failure) {
  if (failure) return

  const moduleCode = to.meta?.moduleCode
  const moduleConfig = moduleCode && STAGE_TRACKING_CONFIG[moduleCode]
  if (!moduleConfig) return

  const routeConfig = moduleConfig.routes[to.name]
  if (!routeConfig) return

  const studyId = to.params?.id
  if (!studyId) return

  const { track } = useAnalytics()
  track('Stage Entered', {
    study_id: namespacedValue(moduleConfig.recordType, studyId),
    stage: namespacedValue(moduleCode, routeConfig.stage ?? to.name),
    funnel_stage: namespacedValue(moduleCode, routeConfig.funnelStage),
    module: moduleCode
  })
}
